import styled from '@emotion/styled';

import type { SpacingScaleNumber } from '@/types/tokens';
import type { HTMLAttributes, ReactNode } from 'react';

type BorderRadiusSizing = 'xs' | 's' | 'm' | 'l';

export interface CardProps extends HTMLAttributes<HTMLElement> {
  padding?: SpacingScaleNumber;
  horizontalPadding?: SpacingScaleNumber;
  verticalPadding?: SpacingScaleNumber;
  /**  Adds a 'blur' to the background of the card */
  backdropBlur?: boolean;
  header?: ReactNode;
  children?: ReactNode;
  borderRadius?: BorderRadiusSizing;
  zoomOnHover?: boolean;
}

export const Card = ({
  children,
  header,
  padding,
  horizontalPadding,
  verticalPadding,
  borderRadius = 'm',
  ...props
}: CardProps) => {
  return (
    <CardContainer borderRadius={borderRadius} {...props}>
      {header}
      <CardBody
        padding={padding}
        horizontalPadding={horizontalPadding}
        verticalPadding={verticalPadding}
      >
        {children}
      </CardBody>
    </CardContainer>
  );
};

const CardContainer = styled.article<CardProps>`
  position: relative;

  ${(p) =>
    p.borderRadius &&
    `
  border-radius: var(--radius-${p.borderRadius});
`}

  backdrop-filter: ${(p) => (p.backdropBlur ? 'blur(12px)' : 'none')};
  box-shadow: var(--shadow-subtle);
  transition: transform 0.2s ease;

  ${({ zoomOnHover }) =>
    zoomOnHover &&
    `&:hover {
    transform: scale(1.03);
    cursor: pointer;
  }`}
`;

export const CardBody = styled.div<
  Pick<CardProps, 'padding' | 'horizontalPadding' | 'verticalPadding'>
>`
  height: 100%;
  padding: var(--spacing-${(p) => p.padding ?? 250});
  ${(p) =>
    p.verticalPadding &&
    `
    padding-top: var(--spacing-${p.verticalPadding});
    padding-bottom: var(--spacing-${p.verticalPadding});
  `}
  ${(p) =>
    p.horizontalPadding &&
    `
    padding-left: var(--spacing-${p.horizontalPadding});
    padding-right: var(--spacing-${p.horizontalPadding});
  `}
`;
